import {
  Box,
  ButtonBase,
  Grow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
} from '@mui/material';
import { EntityName } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PlatformUserAvatar } from 'src/components';
import { StyledAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useCurrentUserProfile } from 'src/graphql';
import {
  EntityViewer,
  EntityViewerStatus,
} from 'src/graphql/entities/entity-viewer/entity-viewer.types';
import { usePlatformUserAvatarLookup } from 'src/graphql/entities/platform-user/use-platform-user-avatar-lookup';
import { formatLastMessageSinceTime } from 'src/utils/date';
import { useViewerPresence } from './hooks/use-viewer-presence';

interface ViewerListStatefulProps {
  entity: EntityName;
  entityId?: string;
  /** Hides the avatars but keeps this user's presence published, which unmounting would stop. */
  isHidden?: boolean;
}

const getId = (v: { firstName?: string; lastName?: string }) =>
  `${v.firstName ?? ''}|${v.lastName ?? ''}`;

const MAX_ITEMS = 3;

const SEPARATOR_RING_WIDTH = '2.5px';
const ACTIVE_RING_WIDTH = '2px';
const OVERLAP = '-0.25rem';

export const ViewerListStateful = ({
  entity,
  entityId,
  isHidden = false,
}: ViewerListStatefulProps) => {
  const { user } = useCurrentUserProfile();
  const { viewers } = useViewerPresence({ entity, entityId });

  const viewerIds = useMemo(() => viewers.map((v) => v.viewerId), [viewers]);
  const avatarBitsByViewerId = usePlatformUserAvatarLookup(viewerIds);

  const [overflowOpen, setOverflowOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const overflowAnchorRef = useRef<HTMLButtonElement | null>(null);

  const openNow = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOverflowOpen(true);
  };

  const scheduleClose = (delay = 120) => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(
      () => setOverflowOpen(false),
      delay,
    ) as unknown as number;
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const prevStatusRef = useRef<Map<string, string>>(new Map());
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      viewers.forEach((v) => prevStatusRef.current.set(getId(v), v.status));
      return;
    }
    const now: string[] = [];
    viewers.forEach((v) => {
      const id = getId(v);
      const prev = prevStatusRef.current.get(id);

      if (v.status === EntityViewerStatus.active && prev !== EntityViewerStatus.active) {
        now.push(id);
      }

      prevStatusRef.current.set(id, v.status);
    });
  }, [viewers]);

  // active first, then most recent
  const sortedItems = viewers
    .map((i) => ({
      ...i,
      status: i.viewerId === user?.id ? EntityViewerStatus.active : i.status,
    }))
    .toSorted((a, b) => {
      const aInactive = a.status === EntityViewerStatus.inactive ? 1 : 0;
      const bInactive = b.status === EntityViewerStatus.inactive ? 1 : 0;
      if (aInactive !== bInactive) return aInactive - bInactive;
      return b.lastSeen - a.lastSeen;
    });

  const visible = sortedItems.slice(0, MAX_ITEMS);
  const overflow = sortedItems.slice(MAX_ITEMS);
  const total = visible.length;

  if (!viewers?.length) {
    return null;
  }

  return (
    <Box display={isHidden ? 'none' : 'flex'} flexDirection="row">
      {visible.map((viewer, i) => {
        const fullName = getFullName(viewer) || 'Unknown';
        const key = `${viewer.firstName ?? ''}-${viewer.lastName ?? ''}-${i}`;
        const isActive = viewer.status === EntityViewerStatus.active;

        const title = (
          <Box display="flex" flexDirection="column">
            <Box>{fullName}</Box>
            <Box sx={{ fontSize: '10px' }}>{getViewingLabel(viewer)}</Box>
          </Box>
        );

        const avatarBits = avatarBitsByViewerId.get(viewer.viewerId);

        const avatarNode = (
          <Tooltip title={title} enterDelay={10}>
            <span
              style={{
                display: 'inline-flex',
                opacity: isActive ? 1 : 0.45,
                transition: 'opacity 120ms ease-out',
              }}
            >
              <PlatformUserAvatar
                name={fullName}
                partnerId={avatarBits?.partnerId}
                avatarFileKey={avatarBits?.avatarFileKey}
                sx={{
                  width: '1.75rem',
                  height: '1.75rem',
                  fontSize: '0.45rem',
                }}
              />
            </span>
          </Tooltip>
        );

        return (
          <Box
            key={key}
            sx={{
              position: 'relative',
              zIndex: total - i,
              marginLeft: i === 0 ? 0 : OVERLAP,
              borderRadius: '50%',
              width: '1.75rem',
              height: '1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
              boxShadow: (theme) =>
                isActive
                  ? `0 0 0 ${ACTIVE_RING_WIDTH} ${theme.palette.custom.OV.sys.success}`
                  : `0 0 0 ${SEPARATOR_RING_WIDTH} ${theme.palette.custom.OV.sys.surfaceContainerLowest}`,
              transition: 'box-shadow 120ms ease-out',
            }}
          >
            <Grow in appear timeout={360} style={{ transformOrigin: 'center' }}>
              <Box>{avatarNode}</Box>
            </Grow>
          </Box>
        );
      })}

      {overflow.length > 0 && (
        <Box
          sx={{
            position: 'relative',
            marginLeft: total === 0 ? 0 : OVERLAP,
            zIndex: 0,
            borderRadius: '50%',
            backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
            boxShadow: (theme) =>
              `0 0 0 ${SEPARATOR_RING_WIDTH} ${theme.palette.custom.OV.sys.surfaceContainerLowest}`,
          }}
        >
          <ButtonBase
            ref={overflowAnchorRef}
            onMouseEnter={openNow}
            onMouseLeave={() => scheduleClose()}
            sx={{ borderRadius: '50%' }}
          >
            <StyledAvatar
              backgroundColor="hsl(220, 10%, 50%)"
              sx={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
            >
              {`+${overflow.length}`}
            </StyledAvatar>
          </ButtonBase>

          <Popper
            open={overflowOpen}
            anchorEl={overflowAnchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: (t) => t.zIndex.modal }}
            onMouseEnter={openNow}
            onMouseLeave={() => scheduleClose()}
          >
            {/* hover bridge covers the 4px gap between trigger and Paper */}
            <Box sx={{ height: 6 }} />
            <Paper
              elevation={3}
              sx={{
                minWidth: 220,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              <List dense disablePadding>
                {overflow.map((viewer, i) => {
                  const fullName = getFullName(viewer) || 'Unknown';
                  const key = `overflow-${viewer.firstName ?? ''}-${viewer.lastName ?? ''}-${i}`;

                  return (
                    <ListItem key={key} disablePadding sx={{ padding: 0, margin: 0 }}>
                      <ListItemButton onClick={() => scheduleClose(0)}>
                        <ListItemText
                          primary={fullName}
                          secondary={getViewingLabel(viewer)}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Popper>
        </Box>
      )}
    </Box>
  );
};

function getViewingLabel(viewer: EntityViewer) {
  const lastViewedRel = formatLastMessageSinceTime(toDateTime(viewer.lastSeen));

  return viewer.status === EntityViewerStatus.active
    ? 'Viewing now'
    : `Last viewed ${formatSeenAgoTime(lastViewedRel)}`;
}

function formatSeenAgoTime(lastViewedRel: string) {
  // 2 words mean value + unit, like 2 hours, therefore we can
  // safely show "ago", otherwise it's a date a custom label
  const withAgoPrefix = lastViewedRel.split(' ').length === 2;

  return `${lastViewedRel} ${withAgoPrefix ? 'ago' : ''}`;
}

function toDateTime(secs: number) {
  return new Date(secs * 1000).toISOString();
}
